import type { LscMember, LscVodSyncReadiness, LscVodSyncReadinessReason } from './types';

export const VOD_SYNC_READINESS_COPY = {
  headline: 'Only active members with linked Twitch accounts are scanned.',
  pendingLink:
    'Pending-link members must join, claim their profile, and connect Twitch before their VODs can be synced.',
  noGuess:
    'LSC does not guess Twitch handles from Discord, NeonSportz, PSN, or gamer tags.',
  syncReadyDefinition:
    'Sync-ready members are active league members with verified Twitch accounts.',
  skippedDefinition: 'Skipped members are not scanned.',
} as const;

export const VOD_SYNC_REASON_LABELS: Record<LscVodSyncReadinessReason, string> = {
  sync_ready: 'Sync ready',
  inactive: 'Inactive',
  pending_link: 'Pending Link',
  missing_twitch: 'Missing Twitch',
  not_active: 'Not active',
  unknown: 'Unknown',
};

export function formatVodSyncReadinessHeadline(readiness: LscVodSyncReadiness): string {
  return `${readiness.eligible_count} of ${readiness.total_current_members} current members`;
}

export function formatVodSyncSkippedHeadline(readiness: LscVodSyncReadiness): string {
  if (readiness.skipped_count <= 0) {
    return 'All current members are sync-ready';
  }
  return `${readiness.skipped_count} members need attention`;
}

export function vodSyncReasonCounts(readiness: LscVodSyncReadiness): Array<{
  reason: LscVodSyncReadinessReason;
  count: number;
  label: string;
}> {
  const counts = readiness.reason_counts ?? {};
  return (Object.keys(VOD_SYNC_REASON_LABELS) as LscVodSyncReadinessReason[])
    .map((reason) => ({
      reason,
      count: counts[reason] ?? 0,
      label: VOD_SYNC_REASON_LABELS[reason],
    }))
    .filter((entry) => entry.count > 0 && entry.reason !== 'sync_ready');
}

export function getMemberVodSyncReadinessLabel(member: LscMember): {
  label: string;
  ready: boolean;
} {
  if (member.status === 'inactive') {
    return { label: 'Inactive', ready: false };
  }
  if (member.status === 'imported_unclaimed') {
    return { label: 'Pending Link', ready: false };
  }
  if (member.status === 'invited') {
    return { label: 'Pending Link', ready: false };
  }
  if (member.status !== 'active') {
    return { label: 'Not ready', ready: false };
  }
  if (member.twitch_user_id || member.twitch_login || member.user_uid) {
    return { label: 'Sync Ready', ready: true };
  }
  return { label: 'Missing Twitch', ready: false };
}

export function formatDashboardVodSyncDetail(readiness: LscVodSyncReadiness): string {
  const needsAttention = readiness.skipped_count;
  if (needsAttention <= 0) {
    return 'All current members are sync-ready';
  }
  return `${needsAttention} need Twitch/profile link`;
}
